/**
 * Entity - Base class for all game entities
 */

import { Vector3 } from '../math/Vector3';
import { Component } from './Component';
import { ComponentType } from '../types/enums';
import { Renderer } from '../core/Renderer';

let nextEntityId = 1;

export abstract class Entity {
  readonly id: number;
  position: Vector3;
  isActive: boolean = true;
  protected components: Map<ComponentType, Component> = new Map();

  constructor(position: Vector3) {
    this.id = nextEntityId++;
    this.position = position;
  }

  /** Add a component to this entity */
  addComponent<T extends Component>(component: T): T {
    component.attach(this);
    this.components.set(component.type, component);
    return component;
  }

  /** Remove a component from this entity */
  removeComponent(type: ComponentType): void {
    const component = this.components.get(type);
    if (component) {
      component.detach();
      this.components.delete(type);
    }
  }

  /** Get a component by type */
  getComponent<T extends Component>(type: ComponentType): T | undefined {
    return this.components.get(type) as T | undefined;
  }

  /** Check if entity has a component */
  hasComponent(type: ComponentType): boolean {
    return this.components.has(type);
  }

  /** Update entity and all components */
  update(deltaTime: number): void {
    for (const component of this.components.values()) {
      component.update(deltaTime);
    }
  }

  /** Render entity */
  abstract render(renderer: Renderer): void;

  /** Destroy entity */
  destroy(): void {
    this.isActive = false;
    for (const component of this.components.values()) {
      component.detach();
    }
    this.components.clear();
  }

  /** Get distance to another entity */
  distanceTo(other: Entity): number {
    return this.position.distance(other.position);
  }

  /** Get distance to a position */
  distanceToPoint(point: Vector3): number {
    return this.position.distance(point);
  }
}
